import { memo, useCallback } from 'react'
import type { Card } from '../../types'
import { motion } from 'framer-motion'
import { ArrowLeft, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { getCardPadding, getScaledFontSize, getScaledIconSize } from '../../utils/gridLayout'
import { useCardStore } from '../../store/cardStore'

interface LinkCardProps {
  card: Card
  editMode: boolean
  cellSize: number
}

const LinkCard = memo(function LinkCard({ card, editMode, cellSize }: LinkCardProps) {
  const { url, label, linkAlign = 'right', linkSubpage } = card.content
  const subpages = useCardStore((state) => state.subpages)
  const fetchCards = useCardStore((state) => state.fetchCards)
  const setCurrentPageId = useCardStore((state) => state.setCurrentPageId)
  const setIsPublicTransitioning = useCardStore((state) => state.setIsPublicTransitioning)
  const navigate = useNavigate()

  const padding = getCardPadding(cellSize)
  const fontSize = getScaledFontSize(cellSize, 16)
  const iconSize = getScaledIconSize(cellSize, 18)

  const normalizedAlign = linkAlign === 'left' ? 'left' : 'right'

  const isLeft = normalizedAlign === 'left'
  const alignClass = isLeft ? 'text-right' : 'text-left'

  const normalizeExternalUrl = (raw: string) => {
    const trimmed = raw.trim()
    if (!trimmed) return ''
    if (/^https?:\/\//i.test(trimmed)) return trimmed
    return `https://${trimmed}`
  }

  const renderEmphasis = (input: string) => {
    const parts = input.split(/(\*[^*]+\*)/g)
    return parts.map((part, i) => {
      if (part.startsWith('*') && part.endsWith('*') && part.length >= 3) {
        return (
          <span key={i} className="font-semibold">
            {part.slice(1, -1)}
          </span>
        )
      }
      return <span key={i}>{part}</span>
    })
  }

  const getSubdomainFromHostname = (hostname: string): string => {
    const host = hostname.split(':')[0].toLowerCase()
    if (!host) return ''

    if (host === 'oda.bio' || host === 'www.oda.bio') return ''
    if (host.endsWith('.oda.bio')) {
      const sub = host.slice(0, -'.oda.bio'.length)
      return sub === 'www' ? '' : sub
    }
    return ''
  }

  const getProfileBasePath = () => {
    const parts = window.location.pathname.split('/').filter(Boolean)
    if (parts.length === 0) return ''

    const [first] = parts
    if (!first) return ''
    if (first === 'edit') return ''

    return first
  }

  const buildInternalUrl = (slug: string) => {
    const hostSub = getSubdomainFromHostname(window.location.hostname)
    const isSubdomainHost = !!hostSub
    const base = !isSubdomainHost ? getProfileBasePath() : ''

    const isMain = slug === '__main__'
    if (isSubdomainHost) return isMain ? '/' : `/${slug}`
    if (!base) return isMain ? '/' : `/${slug}`
    return isMain ? `/${base}` : `/${base}/${slug}`
  }

  const isEditorPreview = () => {
    const parts = window.location.pathname.split('/').filter(Boolean)
    return parts[0] === 'edit'
  }

  const openEditorSubpage = async (slug: string) => {
    const targetPageId = slug === '__main__'
      ? null
      : (subpages.find((sp) => sp.slug === slug)?.id ?? null)

    if (slug !== '__main__' && targetPageId === null) return

    setCurrentPageId(targetPageId)
    await fetchCards(targetPageId)

    const main = document.getElementById('main-scroll-container')
    if (main) {
      main.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  const handleClick = useCallback(async (e: React.MouseEvent) => {
    if (editMode) {
      e.preventDefault()
      return
    }
    if (linkSubpage) {
      e.preventDefault()
      if (isEditorPreview()) {
        void openEditorSubpage(linkSubpage)
        return
      }

      const targetUrl = buildInternalUrl(linkSubpage)
      if (window.location.pathname === targetUrl) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return
      }

      setIsPublicTransitioning(true)
      await new Promise(r => setTimeout(r, 200))
      navigate(targetUrl)
      window.scrollTo(0, 0)
      setIsPublicTransitioning(false)
      return
    }
    if (url) {
      const external = normalizeExternalUrl(url)
      if (external) window.open(external, '_blank', 'noopener,noreferrer')
    }
  }, [editMode, url, linkSubpage, subpages, fetchCards, setCurrentPageId])

  return (
    <motion.button
      onClick={handleClick}
      className={`w-full h-full theme-card flex items-center font-serif font-bold transition-colors cursor-pointer ${isLeft ? 'flex-row-reverse' : ''
        }`}
      style={{ paddingLeft: `${padding}px`, paddingRight: `${padding}px`, gap: `${padding * 0.5}px` }}
      whileTap={{ scale: editMode ? 1 : 0.98 }}
    >
      <span className={`flex-1 min-w-0 ${alignClass} whitespace-normal break-words`} style={{ fontSize: `${fontSize}px` }}>
        {renderEmphasis(label || 'Link')}
      </span>
      {isLeft ? <ArrowLeft size={iconSize} /> : <ArrowRight size={iconSize} />}
    </motion.button>
  )
})

export default LinkCard
